const MINUTE_LIMIT_CODES = new Set([
  "OPENROUTE_MINUTE_LIMIT",
  "OPENROUTE_LOCAL_MINUTE_LIMIT",
]);

const DAILY_LIMIT_CODES = new Set([
  "OPENROUTE_DAILY_QUOTA",
  "OPENROUTE_LOCAL_DAILY_LIMIT",
]);

const PROVIDER_UNAUTHORIZED_CODE = "OPENROUTE_API_KEY_UNAUTHORIZED";
const ONE_MINUTE_MS = 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function getHeader(headers, name) {
  if (!headers) {
    return null;
  }

  if (typeof headers.get === "function") {
    return headers.get(name) || headers.get(name.toLowerCase()) || null;
  }

  const matchingKey = Object.keys(headers).find(
    (key) => key.toLowerCase() === name.toLowerCase()
  );

  return matchingKey ? headers[matchingKey] : null;
}

function parsePositiveSeconds(value) {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

function parseFutureDate(value, now) {
  if (!value) {
    return null;
  }

  const parsedValue = Date.parse(value);

  return Number.isFinite(parsedValue) && parsedValue > now ? parsedValue : null;
}

function resolveRetryAt(error, code, now) {
  const response = error?.response;
  const responseData = response?.data || {};
  const retryAfterHeader = getHeader(response?.headers, "Retry-After");
  const headerSeconds = parsePositiveSeconds(retryAfterHeader);

  if (headerSeconds !== null) {
    return now + headerSeconds * 1000;
  }

  const headerDate = parseFutureDate(retryAfterHeader, now);

  if (headerDate !== null) {
    return headerDate;
  }

  const responseSeconds = parsePositiveSeconds(responseData.retryAfterSeconds);

  if (responseSeconds !== null) {
    return now + responseSeconds * 1000;
  }

  const responseDate = parseFutureDate(responseData.retryAt, now);

  if (responseDate !== null) {
    return responseDate;
  }

  return now + (DAILY_LIMIT_CODES.has(code) ? ONE_DAY_MS : ONE_MINUTE_MS);
}

function extractGeocodingAddress(message) {
  const match = String(message || "").match(
    /Failed to geocode address:\s*(.+?)(?:\s+(?:destination|optimize|profile|include_geometry)=|$)/i
  );

  return match?.[1]?.trim() || "";
}

export function buildRoutingRequestKey(config = {}) {
  const method = String(config.method || "get").toLowerCase();
  const url = String(config.url || "");
  const params = config.params ? JSON.stringify(config.params) : "";
  const data = config.data ? JSON.stringify(config.data) : "";

  return `${method}:${url}:${params}:${data}`;
}

export function getRoutingErrorFeedback(error, now = Date.now()) {
  if (error?.routingCooldown) {
    return {
      status: 503,
      code: error.routingCooldown.code || null,
      message: error.routingCooldown.message,
      cooldown: error.routingCooldown,
    };
  }

  const status = Number(error?.response?.status) || null;
  const responseData = error?.response?.data || {};
  const code = responseData.code || null;
  const providerMessage = String(responseData.message || "").trim();

  if (code === PROVIDER_UNAUTHORIZED_CODE) {
    return {
      status,
      code,
      message:
        "A credencial do provedor de roteamento foi recusada. Acione a equipe responsável pela API; não tente novamente agora.",
      cooldown: null,
    };
  }

  if (status === 503) {
    const retryAt = resolveRetryAt(error, code, now);
    let message =
      "O serviço de roteamento está temporariamente indisponível. Aguarde o prazo indicado para tentar novamente.";

    if (MINUTE_LIMIT_CODES.has(code)) {
      message =
        "O limite de consultas por minuto foi atingido. Aguarde o prazo indicado para tentar novamente.";
    } else if (DAILY_LIMIT_CODES.has(code)) {
      message =
        "A cota diária do serviço de roteamento foi atingida. Aguarde a liberação indicada pela API.";
    }

    return {
      status,
      code,
      message,
      cooldown: {
        code,
        message,
        retryAt,
      },
    };
  }

  if (/Failed to geocode address/i.test(providerMessage)) {
    const address = extractGeocodingAddress(providerMessage);

    return {
      status,
      code,
      message: address
        ? `A API de roteamento não conseguiu localizar “${address}”. Tente novamente e, se o problema persistir, acione a equipe responsável pela API.`
        : "A API de roteamento não conseguiu localizar uma das cidades. Tente novamente e, se o problema persistir, acione a equipe responsável pela API.",
      cooldown: null,
    };
  }

  if (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || "")) {
    return {
      status,
      code,
      message:
        "A API de roteamento demorou mais que o esperado para responder. Tente novamente uma única vez.",
      cooldown: null,
    };
  }

  if (!error?.response) {
    return {
      status,
      code,
      message:
        "Não foi possível conectar à API de roteamento. Verifique a rede e tente novamente.",
      cooldown: null,
    };
  }

  return {
    status,
    code,
    message:
      "Não foi possível processar o roteamento. Tente novamente e, se o problema persistir, acione a equipe responsável pela API.",
    cooldown: null,
  };
}

export function formatRoutingCooldown(remainingMilliseconds) {
  const totalSeconds = Math.max(
    0,
    Math.ceil(Number(remainingMilliseconds || 0) / 1000)
  );
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }

  if (minutes > 0) {
    return `${minutes}min ${String(seconds).padStart(2, "0")}s`;
  }

  return `${seconds}s`;
}

export const ROUTING_PROVIDER_UNAUTHORIZED_CODE = PROVIDER_UNAUTHORIZED_CODE;
